package com.upcominds.expedite.features.quiz

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.dto.QuestionnaireQuestionDTO
import com.upcominds.expedite.model.QuizOption
import com.upcominds.expedite.model.QuizResult
import com.upcominds.expedite.model.QuizTimer
import com.upcominds.expedite.dto.QuestionnaireAttemptDTO
import com.upcominds.expedite.model.Challenge
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface QuizStage {
    data class Start(
        val title: String,
        val description: String,
        val questionCount: Int,
        val infoUrl: String? = null,
        val challenge: Challenge? = null
    ) : QuizStage

    data class Question(
        val questionId: String,
        val questionText: String,
        val options: List<QuizOption>,
        val index: Int,
        val totalQuestions: Int,
        val selectedOptionId: String?,
        val isReadOnly: Boolean = false,
        val correctOptionId: String? = null,
        val isAnswerCorrect: Boolean? = null
    ) : QuizStage

    data class Finished(
        val title: String,
        val result: QuizResult,
        val questions: List<QuestionnaireQuestionDTO>
    ) : QuizStage
}

data class QuizUiState(
    val loading: Boolean = false,
    val stage: QuizStage? = null,
    val error: String? = null,
)

class QuizViewModel(private val challengesRepository: ChallengesRepository) : ViewModel() {

    private val _uiState = MutableStateFlow(QuizUiState())
    val uiState: StateFlow<QuizUiState> = _uiState.asStateFlow()

    private var attempt: QuestionnaireAttemptDTO? = null
    private var challenge: Challenge? = null
    private var answers: MutableMap<String, String> = mutableMapOf() // questionId -> optionId
    private var timer: QuizTimer = QuizTimer()
    private var isCompleted: Boolean = false

    fun loadQuiz(challengeId: String, reviewOnly: Boolean = false) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                // Load challenge details first
                try {
                    challenge = challengesRepository.getChallenge(challengeId)
                } catch (e: Exception) {
                    logMobileError("QuizViewModel.loadQuiz.challenge", e, "Failed to preload challenge $challengeId")
                    // Fallback or non-critical error
                }

                val questionnaireAttempt = if (reviewOnly) {
                    challengesRepository.questionnaireReview(challengeId)
                } else {
                    challengesRepository.questionnaireStart(challengeId)
                }
                this@QuizViewModel.attempt = questionnaireAttempt
                isCompleted = reviewOnly || questionnaireAttempt.completed

                answers.clear()
                questionnaireAttempt.questions.forEach { q ->
                    q.selectedOptionId?.let { answers[q.id] = it }
                }

                if (isCompleted) {
                    showFinished()
                } else {
                    val isStarted = answers.isNotEmpty()

                    if (isStarted) {
                        timer = QuizTimer().startTimer()
                        val firstUnanswered = questionnaireAttempt.questions.indexOfFirst { it.id !in answers }.let { if (it == -1) 0 else it }
                        showQuestion(firstUnanswered)
                    } else {
                        _uiState.value = _uiState.value.copy(
                            stage = QuizStage.Start(
                                title = questionnaireAttempt.name.ifBlank { challenge?.name ?: "Questionnaire" },
                                description = questionnaireAttempt.description ?: challenge?.description ?: "Please answer the following questions.",
                                questionCount = questionnaireAttempt.questions.size,
                                infoUrl = questionnaireAttempt.infoUrl,
                                challenge = challenge
                            )
                        )
                    }
                }

                _uiState.value = _uiState.value.copy(loading = false)
            } catch (e: Exception) {
                logMobileError("QuizViewModel.loadQuiz", e, "Failed to load quiz for challenge $challengeId")
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message ?: "Failed to load questionnaire",
                )
            }
        }
    }

    fun startQuestions() {
        timer = QuizTimer().startTimer()
        val firstUnanswered = attempt?.questions?.indexOfFirst { it.id !in answers }?.let { if (it == -1) 0 else it } ?: 0
        showQuestion(firstUnanswered)
    }

    private fun showQuestion(index: Int) {
        val currentAttempt = attempt ?: return
        if (index < 0 || index >= currentAttempt.questions.size) return

        val question = currentAttempt.questions[index]
        _uiState.value = _uiState.value.copy(
            stage = QuizStage.Question(
                questionId = question.id,
                questionText = question.text,
                options = question.options.map { QuizOption(it.id, it.text) },
                index = index,
                totalQuestions = currentAttempt.questions.size,
                selectedOptionId = answers[question.id],
                isReadOnly = isCompleted
            )
        )
    }

    fun selectAnswer(questionId: String, optionId: String) {
        val stage = _uiState.value.stage
        if (stage !is QuizStage.Question || stage.selectedOptionId != null || isCompleted) return

        viewModelScope.launch {
            try {
                val currentAttempt = attempt ?: return@launch
                challengesRepository.questionnaireSubmitAnswer(currentAttempt.attemptId, questionId, optionId)

                answers[questionId] = optionId
                _uiState.value = _uiState.value.copy(
                    stage = stage.copy(selectedOptionId = optionId)
                )
            } catch (e: Exception) {
                logMobileError("QuizViewModel.selectAnswer", e, "Failed to submit quiz answer for question $questionId")
                _uiState.value = _uiState.value.copy(error = "Failed to submit answer: ${e.message}")
            }
        }
    }

    fun next() {
        val stage = _uiState.value.stage as? QuizStage.Question ?: return
        if (answers[stage.questionId] == null) return

        val nextIndex = stage.index + 1
        val currentAttempt = attempt ?: return

        if (nextIndex < currentAttempt.questions.size) {
            showQuestion(nextIndex)
        } else {
            if (isCompleted) {
                showFinished()
            } else {
                finish()
            }
        }
    }

    fun previous() {
        val stage = _uiState.value.stage as? QuizStage.Question ?: return
        val prevIndex = stage.index - 1
        if (prevIndex >= 0) {
            showQuestion(prevIndex)
        }
    }

    private fun showFinished() {
        val currentAttempt = attempt ?: return

        val questionsWithAnswers = currentAttempt.questions.map { question ->
            answers[question.id]?.let {
                question.copy(selectedOptionId = it)
            } ?: question
        }

        _uiState.value = _uiState.value.copy(
            stage = QuizStage.Finished(
                title = currentAttempt.name.ifBlank { "Questionnaire Result" },
                result = QuizResult(
                    totalQuestions = currentAttempt.questions.size,
                    correctAnswers = answers.size,
                    elapsedSeconds = timer.elapsedSeconds,
                    score = 0,
                    pointsAwarded = 0
                ),
                questions = questionsWithAnswers
            )
        )
    }

    private fun finish() {
        timer = timer.stopTimer()
        val currentAttempt = attempt ?: return

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true)
            try {
                challengesRepository.questionnaireComplete(currentAttempt.attemptId)
                isCompleted = true
                showFinished()
                _uiState.value = _uiState.value.copy(loading = false)
            } catch (e: Exception) {
                logMobileError("QuizViewModel.finish", e, "Failed to complete quiz attempt ${currentAttempt.attemptId}")
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = "Failed to complete questionnaire: ${e.message}"
                )
            }
        }
    }

    fun restart() {
        answers.clear()
        attempt = null
        isCompleted = false
    }
}
